import React, { useEffect, useState } from "react";
import { Panel, Field } from "@/pages/admin/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/pages/admin/api";

const FONTS = ["Manrope", "IBM Plex Sans", "Inter", "Space Grotesk", "Poppins", "Roboto", "Nunito", "Fira Sans"];

export default function ThemeSettings() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings/theme").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const set = (k, v) => setData({ ...data, [k]: v });

  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/settings/theme", data); setData(r.data); toast.success("Theme updated"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <Panel title="Theme Colors" actions={<Button data-testid="save-theme" onClick={save} disabled={saving} className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white"><Save size={14} className="mr-1" /> {saving ? "Saving..." : "Save"}</Button>}>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Primary Color">
            <div className="flex items-center gap-2">
              <input type="color" value={data.primary_color} onChange={(e) => set("primary_color", e.target.value)} className="w-12 h-10 rounded-lg border border-border cursor-pointer" />
              <Input value={data.primary_color} onChange={(e) => set("primary_color", e.target.value)} />
            </div>
          </Field>
          <Field label="Secondary Color">
            <div className="flex items-center gap-2">
              <input type="color" value={data.secondary_color} onChange={(e) => set("secondary_color", e.target.value)} className="w-12 h-10 rounded-lg border border-border cursor-pointer" />
              <Input value={data.secondary_color} onChange={(e) => set("secondary_color", e.target.value)} />
            </div>
          </Field>
        </div>
      </Panel>

      <Panel title="Typography">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Heading Font">
            <Select value={data.font_heading} onValueChange={(v) => set("font_heading", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{FONTS.map((f) => <SelectItem key={f} value={f} style={{ fontFamily: f }}>{f}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Body Font">
            <Select value={data.font_body} onValueChange={(v) => set("font_body", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{FONTS.map((f) => <SelectItem key={f} value={f} style={{ fontFamily: f }}>{f}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
      </Panel>

      <Panel title="Styling">
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Border Radius (px)"><Input type="number" min="0" max="32" value={data.border_radius} onChange={(e) => set("border_radius", Number(e.target.value))} /></Field>
          <Field label="Button Style">
            <Select value={data.button_style} onValueChange={(v) => set("button_style", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="rounded">Rounded</SelectItem>
                <SelectItem value="pill">Pill</SelectItem>
                <SelectItem value="square">Square</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Default Dark Mode">
            <label className="flex items-center gap-2 h-10">
              <Checkbox checked={!!data.dark_mode_default} onCheckedChange={(v) => set("dark_mode_default", !!v)} />
              <span className="text-xs text-muted-foreground">{data.dark_mode_default ? "On" : "Off"}</span>
            </label>
          </Field>
          <Field label="Logo URL" className="md:col-span-2"><Input value={data.logo || ""} onChange={(e) => set("logo", e.target.value)} /></Field>
          <Field label="Favicon URL"><Input value={data.favicon || ""} onChange={(e) => set("favicon", e.target.value)} /></Field>
        </div>
      </Panel>
    </div>
  );
}
